import React from 'react';

import { useIntl } from '../../util/reactIntl';
import { SCHEMA_TYPE_ENUM, SCHEMA_TYPE_MULTI_ENUM } from '../../util/types';
import { constructQueryParamName, parseSelectFilterOptions } from '../../util/search';

import chipCloseIcon from '../../assets/search/icon-chip-close.svg';

import css from './ActiveFilterChips.module.css';

/**
 * Selected listing-field values as dismissible pills (Figma 562:707).
 *
 * @param {Object} props
 * @param {Array<Object>} props.availableFilters filter configs from SearchPage.shared
 * @param {Object} props.urlQueryParams current URL query
 * @param {Function} props.getHandleChangedValueFn filter submit factory
 * @returns {JSX.Element|null}
 */
const ActiveFilterChips = props => {
  const { availableFilters = [], urlQueryParams = {}, getHandleChangedValueFn } = props;
  const intl = useIntl();
  const onChange = getHandleChangedValueFn(true);
  const chips = [];

  availableFilters.forEach(config => {
    const { key, schemaType, scope, enumOptions = [], filterConfig = {} } = config;
    const { searchMode } = filterConfig;

    if (schemaType === SCHEMA_TYPE_MULTI_ENUM || schemaType === SCHEMA_TYPE_ENUM) {
      const param = constructQueryParamName(key, scope);
      const raw = urlQueryParams[param];
      if (!raw) {
        return;
      }
      const selected = parseSelectFilterOptions(String(raw)).filter(Boolean);
      selected.forEach(option => {
        const match = enumOptions.find(o => o.option === option);
        chips.push({
          id: `${param}:${option}`,
          label: match?.label || option,
          onRemove: () => {
            const next = selected.filter(value => value !== option);
            const prefix =
              schemaType === SCHEMA_TYPE_MULTI_ENUM && searchMode ? `${searchMode}:` : '';
            onChange({ [param]: next.length > 0 ? `${prefix}${next.join(',')}` : null });
          },
        });
      });
    }
  });

  if (chips.length === 0) {
    return null;
  }

  return (
    <ul className={css.list}>
      {chips.map(chip => (
        <li key={chip.id} className={css.item}>
          <span className={css.label}>{chip.label}</span>
          <button
            type="button"
            className={css.remove}
            onClick={chip.onRemove}
            aria-label={intl.formatMessage(
              { id: 'SearchPage.removeFilter' },
              { label: chip.label }
            )}
          >
            <img src={chipCloseIcon} alt="" width={16} height={16} />
          </button>
        </li>
      ))}
    </ul>
  );
};

export default ActiveFilterChips;
